import { getAppState, logout, logoutGuest } from './index';

import { applyI18n, setLocale, t } from '../i18n';

import { icon, iconImg, type IconName } from '../ui/icons';
import { createHelpOpenButton } from '../help/helpTips';
import { showConfirmDialog } from '../ui/confirmDialog';
import { createGuestShellNameText } from '../ui/guestShellName';
import { createEnvBanner } from './envChrome';
import { sedrApi } from '../api/sedr';
import {
  countPendingSedrDeletionApprovals,
  SEDR_DELETION_PENDING_CHANGED,
} from '../pages/sedr/sedrDeletionPending';
import { isGuestSettingsPath } from './navigationState';

const THEME_KEY = 'shareweb:theme';
const MOBILE_HEADER_MQ = '(max-width: 768px)';
let sedrDeletionBadgeListenerBound = false;

function getTheme(): 'light' | 'dark' {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme;
  localStorage.setItem(THEME_KEY, theme);
}

function hasAnyRole(roles: string[], required: string[]): boolean {
  return required.some((role) => roles.includes(role));
}

type NavItem = {
  href: string;
  icon: IconName;
  labelKey: string;
};

function createNavLink(item: NavItem): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = item.href;
  link.className = 'nav-link';
  const label = document.createElement('span');
  label.setAttribute('data-i18n', item.labelKey);
  link.append(iconImg(item.icon, 18), label);
  return link;
}

function setNavLinkBadge(link: HTMLAnchorElement, count: number, ariaLabel: string): void {
  const existing = link.querySelector('.nav-link-badge');
  if (count <= 0) {
    existing?.remove();
    link.removeAttribute('aria-label');
    return;
  }
  const label = count > 99 ? '99+' : String(count);
  let badge = existing as HTMLElement | null;
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'nav-link-badge';
    badge.setAttribute('aria-hidden', 'true');
    link.append(badge);
  }
  badge.textContent = label;
  link.setAttribute('aria-label', ariaLabel);
}

async function refreshSedrDeletionNavBadge(nav: HTMLElement, userId: string): Promise<void> {
  const sedrLink = nav.querySelector('a.nav-link[href="/sedr"]') as HTMLAnchorElement | null;
  if (!sedrLink || !userId) return;
  try {
    const rooms = await sedrApi.listRooms();
    if (!sedrLink.isConnected) return;
    const count = countPendingSedrDeletionApprovals(rooms, userId);
    const aria = t('home.outstanding.approveDeletion');
    setNavLinkBadge(
      sedrLink,
      count,
      count > 0 ? `${t('nav.sedr')} — ${aria} (${count})` : '',
    );
  } catch {
    // Ignore badge refresh failures; nav remains usable without the count.
  }
}

function refreshSedrDeletionNavBadgeFromDom(): void {
  const state = getAppState();
  if (state.kind !== 'authenticated') return;
  const nav = document.querySelector('.app-shell:not(.app-shell--guest) .app-nav');
  if (!(nav instanceof HTMLElement)) return;
  void refreshSedrDeletionNavBadge(nav, state.session.userId);
}

function ensureSedrDeletionBadgeListener(): void {
  if (sedrDeletionBadgeListenerBound) return;
  sedrDeletionBadgeListenerBound = true;
  window.addEventListener(SEDR_DELETION_PENDING_CHANGED, () => {
    refreshSedrDeletionNavBadgeFromDom();
  });
}

function isNavLinkActive(href: string, currentPath: string): boolean {
  if (href === currentPath) return true;
  return href !== '/' && currentPath.startsWith(`${href}/`);
}

function markActiveNavLinks(nav: HTMLElement): void {
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  for (const link of nav.querySelectorAll('a.nav-link')) {
    const href = link.getAttribute('href') ?? '/';
    link.classList.toggle('nav-link--active', isNavLinkActive(href, currentPath));
  }
}

function resolveActiveNavItem(path: string, items: NavItem[]): NavItem {
  if (path === '/help') {
    return { href: '/help', icon: 'help-circle', labelKey: 'help.title' };
  }
  let best: NavItem | null = null;
  for (const item of items) {
    if (!isNavLinkActive(item.href, path)) continue;
    if (!best || item.href.length > best.href.length) {
      best = item;
    }
  }
  return best ?? items.find((item) => item.href === '/') ?? items[0]!;
}

function updateThemeToggle(button: HTMLButtonElement): void {
  const theme = getTheme();
  const iconName = theme === 'dark' ? 'sun' : 'moon';
  const labelKey = theme === 'dark' ? 'theme.light' : 'theme.dark';
  const label = document.createElement('span');
  label.className = 'header-control-label';
  label.setAttribute('data-i18n', labelKey);
  button.replaceChildren(iconImg(iconName, 18), label);
  button.setAttribute('data-i18n-aria-label', labelKey);
  applyI18n(button);
}

function setMenuChevron(button: HTMLButtonElement, open: boolean): void {
  const chevron = button.querySelector('.app-menu-trigger__chevron');
  if (chevron) {
    chevron.replaceChildren(iconImg(open ? 'chevron-up' : 'chevron-down', 14));
  }
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function createLogoutButton(onConfirm: () => void): HTMLButtonElement {
  const logoutButton = document.createElement('button');
  logoutButton.type = 'button';
  logoutButton.className = 'button ghost';
  logoutButton.innerHTML = `<span data-i18n="auth.logout"></span>`;
  logoutButton.addEventListener('click', () => {
    void (async () => {
      const confirmed = await showConfirmDialog({
        titleKey: 'auth.logoutConfirmTitle',
        messageKey: 'auth.logoutConfirm',
        confirmLabelKey: 'auth.logout',
      });
      if (!confirmed) return;
      logoutButton.disabled = true;
      logoutButton.setAttribute('aria-busy', 'true');
      onConfirm();
    })();
  });
  return logoutButton;
}

function resolveGuestActiveNav(
  options: AppShellGuestOptions,
): { icon: IconName; labelKey: string } {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (options.guestAccount && isGuestSettingsPath(path)) {
    return { icon: 'key', labelKey: 'nav.settings' };
  }
  return GUEST_NAV[options.guestModule];
}

export type GuestShellModule = 'information' | 'files' | 'sedr';

export type AppShellGuestOptions = {
  guestDisplayName?: string;
  guestModule: GuestShellModule;
  /** Hide Sign out after guest session ends. */
  signedOut?: boolean;
  /** Account-guest portal (/guest/*): settings + account logout. */
  guestAccount?: boolean;
};

const GUEST_NAV: Record<
  GuestShellModule,
  { icon: IconName; labelKey: string }
> = {
  information: { icon: 'info-share', labelKey: 'nav.information' },
  files: { icon: 'files', labelKey: 'nav.files' },
  sedr: { icon: 'sedr', labelKey: 'nav.sedr' },
};

export function AppShell(content: HTMLElement, options?: AppShellGuestOptions): HTMLElement {
  applyTheme(getTheme());

  const isGuest = options !== undefined && 'guestModule' in options;
  const shell = document.createElement('div');
  shell.className = isGuest ? 'app-shell app-shell--guest' : 'app-shell';

  const header = document.createElement('header');
  header.className = 'app-header';

  const brandIcon = icon('logo', { label: 'ShareWeb' });
  brandIcon.setAttribute('width', '28');
  brandIcon.setAttribute('height', '28');
  const brandText = document.createElement('span');
  brandText.setAttribute('data-i18n', 'app.name');

  let brand: HTMLElement;
  if (isGuest) {
    if (options?.guestAccount && !options.signedOut) {
      const brandLink = document.createElement('a');
      brandLink.className = 'brand';
      brandLink.href = '/guest/';
      brandLink.append(brandIcon, brandText);
      brand = brandLink;
    } else {
      brand = document.createElement('span');
      brand.className = 'brand';
      brand.append(brandIcon, brandText);
    }
  } else {
    const brandLink = document.createElement('a');
    brandLink.className = 'brand';
    brandLink.href = '/';
    brandLink.append(brandIcon, brandText);
    brand = brandLink;
  }

  const nav = document.createElement('nav');
  nav.className = 'app-nav';
  nav.id = 'app-nav';

  const state = getAppState();
  const navItems: NavItem[] = [];
  let pageMenuButton: HTMLButtonElement | null = null;
  let pageMenuPill: HTMLElement | null = null;
  let accountToggle: HTMLButtonElement | null = null;
  let accountMenu: HTMLElement | null = null;
  let navBackdrop: HTMLButtonElement | null = null;

  if (isGuest) {
    const { icon: navIcon, labelKey } = GUEST_NAV[options!.guestModule];
    if (options?.guestAccount) {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      const onSettings = isGuestSettingsPath(path);

      const sedrLink = document.createElement('a');
      sedrLink.href = '/guest/';
      sedrLink.className = `nav-link${onSettings ? '' : ' nav-link--active'}`;
      if (!onSettings) sedrLink.setAttribute('aria-current', 'page');
      sedrLink.append(iconImg(navIcon, 18), document.createElement('span'));
      (sedrLink.querySelector(':scope > span:last-child') as HTMLSpanElement)
        .setAttribute('data-i18n', labelKey);
      nav.append(sedrLink);

      const settingsLink = document.createElement('a');
      settingsLink.href = '/guest/settings';
      settingsLink.className = `nav-link${onSettings ? ' nav-link--active' : ''}`;
      settingsLink.id = 'guest-shell-settings';
      if (onSettings) settingsLink.setAttribute('aria-current', 'page');
      settingsLink.append(iconImg('key', 18), document.createElement('span'));
      (settingsLink.querySelector(':scope > span:last-child') as HTMLSpanElement)
        .setAttribute('data-i18n', 'nav.settings');
      nav.append(settingsLink);
    } else {
      const guestNav = document.createElement('span');
      guestNav.className = 'nav-link nav-link--active';
      guestNav.setAttribute('aria-current', 'page');
      guestNav.append(iconImg(navIcon, 18), document.createElement('span'));
      const guestLabel = guestNav.querySelector(':scope > span:last-child') as HTMLSpanElement;
      guestLabel.setAttribute('data-i18n', labelKey);
      nav.append(guestNav);
    }
  } else {
    navItems.push(
      { href: '/', icon: 'logo', labelKey: 'nav.home' },
      { href: '/information', icon: 'info-share', labelKey: 'nav.information' },
      { href: '/files', icon: 'files', labelKey: 'nav.files' },
      { href: '/sedr', icon: 'sedr', labelKey: 'nav.sedr' },
    );
    if (state.kind === 'authenticated') {
      if (hasAnyRole(state.session.roles, ['SmsSender', 'Admin'])) {
        navItems.push({ href: '/sms', icon: 'sms', labelKey: 'nav.sms' });
      }
      if (hasAnyRole(state.session.roles, ['Admin', 'Auditor'])) {
        navItems.push({ href: '/admin', icon: 'settings', labelKey: 'nav.admin' });
      }
      navItems.push({ href: '/settings', icon: 'key', labelKey: 'nav.settings' });
    }
    for (const item of navItems) {
      nav.append(createNavLink(item));
    }
    markActiveNavLinks(nav);
    if (state.kind === 'authenticated') {
      ensureSedrDeletionBadgeListener();
      void refreshSedrDeletionNavBadge(nav, state.session.userId);
    }
  }

  const controls = document.createElement('div');
  controls.className = 'header-controls';

  const localeSelect = document.createElement('select');
  localeSelect.setAttribute('data-i18n-aria-label', 'language.label');
  for (const locale of ['en', 'nl', 'fr', 'de'] as const) {
    const option = document.createElement('option');
    option.value = locale;
    option.textContent = locale.toUpperCase();
    localeSelect.appendChild(option);
  }
  localeSelect.value = localStorage.getItem('shareweb:locale') ?? 'en';
  localeSelect.addEventListener('change', () => {
    void setLocale(localeSelect.value).then(() => {
      localeSelect.value = localStorage.getItem('shareweb:locale') ?? localeSelect.value;
    });
  });

  const themeButton = document.createElement('button');
  themeButton.type = 'button';
  themeButton.className = 'icon-button theme-toggle';
  themeButton.addEventListener('click', () => {
    const next = getTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    updateThemeToggle(themeButton);
  });
  updateThemeToggle(themeButton);

  const helpButton = createHelpOpenButton();
  const helpLabel = document.createElement('span');
  helpLabel.className = 'header-control-label';
  helpLabel.setAttribute('data-i18n', 'help.title');
  helpButton.append(helpLabel);

  let logoutButton: HTMLButtonElement | null = null;
  let userLabel: HTMLElement | null = null;
  let displayName = '';
  const useAccountMenuOnMobile = isGuest || state.kind === 'authenticated';
  const showInteractivePageMenu = !isGuest || Boolean(options?.guestAccount);

  if (isGuest) {
    displayName = options?.guestDisplayName ?? '';
    userLabel = document.createElement('span');
    userLabel.className = 'user-label';
    userLabel.id = 'guest-shell-name';
    userLabel.hidden = !displayName;
    userLabel.append(iconImg('user', 16), createGuestShellNameText(displayName));

    if (!options?.signedOut) {
      logoutButton = createLogoutButton(() => {
        void logoutGuest();
      });
    }
  } else if (state.kind === 'authenticated') {
    displayName = state.session.displayName;
    userLabel = document.createElement('span');
    userLabel.className = 'user-label';
    userLabel.append(iconImg('user', 16), document.createTextNode(displayName));

    logoutButton = createLogoutButton(() => {
      void logout();
    });
  }

  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const activeItem = isGuest
    ? resolveGuestActiveNav(options!)
    : resolveActiveNavItem(currentPath, navItems);

  if (showInteractivePageMenu) {
    pageMenuButton = document.createElement('button');
    pageMenuButton.type = 'button';
    pageMenuButton.className = 'app-menu-trigger app-page-menu-trigger';
    pageMenuButton.setAttribute('aria-haspopup', 'true');
    pageMenuButton.setAttribute('aria-controls', 'app-nav');
    pageMenuButton.setAttribute('data-i18n-aria-label', 'nav.menuOpen');
    pageMenuButton.setAttribute('aria-label', t('nav.menuOpen'));

    const pageLabel = document.createElement('span');
    pageLabel.className = 'app-menu-trigger__label';
    pageLabel.setAttribute('data-i18n', activeItem.labelKey);

    const pageChevron = document.createElement('span');
    pageChevron.className = 'app-menu-trigger__chevron';
    pageChevron.setAttribute('aria-hidden', 'true');

    pageMenuButton.append(iconImg(activeItem.icon, 18), pageLabel, pageChevron);
    setMenuChevron(pageMenuButton, false);
  } else {
    pageMenuPill = document.createElement('span');
    pageMenuPill.className = 'app-menu-trigger app-page-menu-trigger app-page-menu-trigger--static';
    pageMenuPill.setAttribute('aria-current', 'page');

    const pageLabel = document.createElement('span');
    pageLabel.className = 'app-menu-trigger__label';
    pageLabel.setAttribute('data-i18n', activeItem.labelKey);

    pageMenuPill.append(iconImg(activeItem.icon, 18), pageLabel);
  }

  navBackdrop = document.createElement('button');
  navBackdrop.type = 'button';
  navBackdrop.className = 'app-nav-backdrop';
  navBackdrop.hidden = true;
  navBackdrop.setAttribute('tabindex', '-1');
  navBackdrop.setAttribute('aria-hidden', 'true');
  navBackdrop.setAttribute('data-i18n-aria-label', 'nav.menuClose');

  accountMenu = document.createElement('div');
  accountMenu.className = 'app-account-menu';
  accountMenu.id = 'app-account-menu';
  accountMenu.hidden = true;

  const fullName = document.createElement('div');
  fullName.className = 'app-account-menu__name';
  if (isGuest) {
    fullName.id = 'guest-shell-account-name';
    fullName.replaceChildren(createGuestShellNameText(displayName || t('app.name')));
  } else {
    fullName.textContent = displayName || t('app.name');
  }

  const localeField = document.createElement('label');
  localeField.className = 'app-account-menu__field';
  const localeFieldLabel = document.createElement('span');
  localeFieldLabel.setAttribute('data-i18n', 'language.label');
  localeField.append(localeFieldLabel, localeSelect);

  accountToggle = document.createElement('button');
  accountToggle.type = 'button';
  accountToggle.className = 'app-menu-trigger app-account-menu-trigger';
  accountToggle.setAttribute('aria-haspopup', 'true');
  accountToggle.setAttribute('aria-controls', 'app-account-menu');
  accountToggle.setAttribute('data-i18n-aria-label', 'nav.accountMenuOpen');
  accountToggle.setAttribute('aria-label', t('nav.accountMenuOpen'));
  accountToggle.hidden = !useAccountMenuOnMobile;

  const accountLabel = document.createElement('span');
  accountLabel.className = 'app-menu-trigger__label';
  accountLabel.textContent = displayName || (isGuest ? t('app.name') : '');

  const accountChevron = document.createElement('span');
  accountChevron.className = 'app-menu-trigger__chevron';
  accountChevron.setAttribute('aria-hidden', 'true');

  accountToggle.append(iconImg('user', 16), accountLabel, accountChevron);
  setMenuChevron(accountToggle, false);

  type OpenMenu = 'none' | 'nav' | 'account';

  const setOpenMenu = (next: OpenMenu) => {
    const navOpen = next === 'nav';
    const accountOpen = next === 'account';
    header.classList.toggle('app-header--nav-open', navOpen);
    header.classList.toggle('app-header--account-open', accountOpen);

    if (pageMenuButton) {
      setMenuChevron(pageMenuButton, navOpen);
      pageMenuButton.setAttribute('data-i18n-aria-label', navOpen ? 'nav.menuClose' : 'nav.menuOpen');
      applyI18n(pageMenuButton);
    }
    if (accountToggle) {
      setMenuChevron(accountToggle, accountOpen);
      accountToggle.setAttribute(
        'data-i18n-aria-label',
        accountOpen ? 'nav.accountMenuClose' : 'nav.accountMenuOpen',
      );
      applyI18n(accountToggle);
    }
    if (accountMenu) {
      accountMenu.hidden = !accountOpen;
    }
    if (navBackdrop) {
      navBackdrop.hidden = next === 'none';
      navBackdrop.setAttribute('aria-hidden', next === 'none' ? 'true' : 'false');
    }
    if (navOpen) {
      const firstLink = nav.querySelector('a.nav-link') as HTMLAnchorElement | null;
      firstLink?.focus();
    } else if (accountOpen) {
      localeSelect.focus();
    }
  };

  const currentOpen = (): OpenMenu => {
    if (header.classList.contains('app-header--nav-open')) return 'nav';
    if (header.classList.contains('app-header--account-open')) return 'account';
    return 'none';
  };

  pageMenuButton?.addEventListener('click', () => {
    setOpenMenu(currentOpen() === 'nav' ? 'none' : 'nav');
  });

  accountToggle.addEventListener('click', () => {
    setOpenMenu(currentOpen() === 'account' ? 'none' : 'account');
  });

  navBackdrop.addEventListener('click', () => {
    setOpenMenu('none');
  });

  nav.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    if (target?.closest('a.nav-link')) {
      setOpenMenu('none');
    }
  });

  shell.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && currentOpen() !== 'none') {
      event.stopPropagation();
      setOpenMenu('none');
    }
  });

  const placeControls = (mobile: boolean) => {
    if (!accountMenu || !accountToggle) return;
    if (mobile && useAccountMenuOnMobile) {
      localeField.replaceChildren(localeFieldLabel, localeSelect);
      accountMenu.replaceChildren(fullName, localeField, themeButton, helpButton);
      if (logoutButton) {
        accountMenu.append(logoutButton);
      }
      controls.replaceChildren(accountToggle);
    } else {
      controls.replaceChildren(localeSelect, themeButton, helpButton);
      if (userLabel) controls.append(userLabel);
      if (logoutButton) controls.append(logoutButton);
    }
  };

  const mq = window.matchMedia(MOBILE_HEADER_MQ);
  const syncLayout = () => {
    placeControls(mq.matches);
    if (!mq.matches) {
      setOpenMenu('none');
    }
  };
  mq.addEventListener('change', syncLayout);
  syncLayout();

  header.append(brand);
  if (pageMenuButton) header.append(pageMenuButton);
  else if (pageMenuPill) header.append(pageMenuPill);
  header.append(nav, controls, accountMenu);

  const main = document.createElement('main');
  main.className = 'app-main';
  main.append(content);

  const envBanner = createEnvBanner();
  if (envBanner) {
    shell.append(envBanner);
  }
  shell.append(header, navBackdrop, main);

  applyI18n(shell);
  return shell;
}
